import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRole } from "../lib/permissions";
import { motion } from "framer-motion";
import logo from "../assets/spacem8-logo.png";
import { logActivity } from "../lib/auditLog";
import {
  CheckCircle2,
  Clock3,
  AlertCircle,
  Wallet,
  Link2,
  Plus,
  Search,
  X,
  Pencil,
  Trash2,
  Printer,
  Mail,
  FileText,
} from "lucide-react";

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

const paymentsTableGridClass =
  "md:grid-cols-[1.2fr_0.9fr_0.7fr_0.8fr_0.9fr_0.8fr_0.8fr_7.5rem]";

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

function getStatusClasses(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Pending") return "bg-sky-100 text-sky-700";
  if (status === "Recorded") return "bg-indigo-100 text-indigo-700";
  if (status === "Partial") return "bg-violet-100 text-violet-700";
  if (status === "Flagged") return "bg-rose-100 text-rose-700";
  return "bg-amber-100 text-amber-800";
}

function isUnmatchedPayment(payment) {
  return Boolean(
    payment?.status === "Flagged" ||
      payment?.unmatched_reason ||
      (!payment?.invoice_id && payment?.provider_transaction_id)
  );
}

function getOutstandingAmount(invoice) {
  if (!invoice) return 0;

  const balanceDue = Number(invoice.balance_due);
  if (Number.isFinite(balanceDue)) {
    return Math.max(balanceDue, 0);
  }

  const amount = Number(invoice.amount || 0);
  const paid = Number(invoice.paidAmount ?? invoice.amount_paid ?? 0);
  return Math.max(amount - paid, 0);
}

function formatUnmatchedReason(reason) {
  const labels = {
    reference_not_found: "Reference not found",
    missing_reference: "Missing reference",
    invoice_already_paid: "Invoice already paid",
  };

  if (!reason) return "Needs reconciliation";
  return labels[reason] || String(reason).replaceAll("_", " ");
}

function scoreReconciliationInvoice(payment, invoice) {
  if (!payment || !invoice) return -1;

  let score = 0;
  const paymentReference = String(payment.reference || "").trim().toLowerCase();
  const invoiceId = String(invoice.id || "").trim().toLowerCase();
  const externalReference = String(invoice.external_reference || "").trim().toLowerCase();

  if (paymentReference && (paymentReference === invoiceId || paymentReference === externalReference)) {
    score += 100;
  }

  if (
    payment.property_name &&
    invoice.property &&
    String(payment.property_name).trim().toLowerCase() === String(invoice.property).trim().toLowerCase()
  ) {
    score += 25;
  }

  if (
    payment.unit &&
    invoice.unit &&
    String(payment.unit).trim().toLowerCase() === String(invoice.unit).trim().toLowerCase()
  ) {
    score += 25;
  }

  if (
    payment.tenant_name &&
    invoice.tenant &&
    String(payment.tenant_name).trim().toLowerCase() === String(invoice.tenant).trim().toLowerCase()
  ) {
    score += 25;
  }

  const invoiceOutstanding = getOutstandingAmount(invoice);
  const paymentAmount = Number(payment.amount || 0);

  if (invoiceOutstanding > 0 && paymentAmount > 0) {
    if (Number(invoiceOutstanding.toFixed(2)) === Number(paymentAmount.toFixed(2))) {
      score += 30;
    } else if (paymentAmount < invoiceOutstanding) {
      score += 10;
    }
  }

  return score;
}

function getInvoiceLabel(invoice) {
  if (!invoice) return "";
  return `${invoice.id} - ${invoice.tenant} - ${invoice.property} - Unit ${invoice.unit}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getReceiptId(payment) {
  return `RCT-${String(payment.id || "").slice(0, 8).toUpperCase() || Date.now()}`;
}

function extractOrganizationId(profile, user, sessionClaims) {
  return (
    profile?.organization_id ||
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    sessionClaims?.app_metadata?.organization_id ||
    sessionClaims?.app_metadata?.organizationId ||
    sessionClaims?.user_metadata?.organization_id ||
    sessionClaims?.user_metadata?.organizationId ||
    ""
  );
}

function getLogoUrl() {
  if (/^https?:\/\//i.test(logo)) return logo;
  return `${window.location.origin}${logo.startsWith("/") ? "" : "/"}${logo}`;
}

function getReceiptHtml(payment, currency) {
  const receiptId = getReceiptId(payment);
  const logoUrl = getLogoUrl();

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(receiptId)}</title>
  <style>
    body { margin: 0; background: #f8fafc; color: #0f172a; font-family: Arial, sans-serif; }
    .receipt { max-width: 760px; margin: 32px auto; background: #fff; padding: 44px; border: 1px solid #e2e8f0; }
    .top { display: flex; justify-content: space-between; gap: 32px; border-bottom: 2px solid #0f172a; padding-bottom: 22px; }
    .brand { display: flex; align-items: center; gap: 14px; }
    .brand img { width: 76px; height: auto; object-fit: contain; }
    h1 { margin: 0; font-size: 32px; letter-spacing: 0.04em; }
    h2 { margin: 0 0 8px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; }
    p { margin: 4px 0; line-height: 1.5; }
    .muted { color: #64748b; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-top: 30px; }
    .amount { margin-top: 34px; border: 1px solid #e2e8f0; padding: 22px; background: #f8fafc; }
    .amount-row { display: flex; justify-content: space-between; gap: 24px; font-size: 22px; font-weight: 700; }
    table { width: 100%; border-collapse: collapse; margin-top: 30px; }
    th { background: #f1f5f9; color: #475569; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; padding: 14px; }
    td { border-bottom: 1px solid #e2e8f0; padding: 16px 14px; }
    .right { text-align: right; }
    .footer { margin-top: 38px; border-top: 1px solid #e2e8f0; padding-top: 18px; color: #64748b; font-size: 13px; }
    @media print { body { background: #fff; } .receipt { margin: 0; max-width: none; border: 0; } }
  </style>
</head>
<body>
  <main class="receipt">
    <section class="top">
      <div class="brand">
        <img src="${escapeHtml(logoUrl)}" alt="SpaceM8" />
        <div>
          <h1>PAYMENT RECEIPT</h1>
          <p class="muted">SpaceM8 Property Management</p>
        </div>
      </div>
      <div class="right">
        <p><strong>${escapeHtml(receiptId)}</strong></p>
        <p>Date: ${escapeHtml(payment.due_date || new Date().toISOString().slice(0, 10))}</p>
        <p>Status: ${escapeHtml(payment.status)}</p>
      </div>
    </section>

    <section class="grid">
      <div>
        <h2>Received From</h2>
        <p><strong>${escapeHtml(payment.tenant_name)}</strong></p>
        <p>${escapeHtml(payment.property_name)}</p>
        <p>Unit ${escapeHtml(payment.unit)}</p>
      </div>
      <div>
        <h2>Payment Details</h2>
        <p>Invoice: ${escapeHtml(payment.invoice_id || "Unlinked")}</p>
        <p>Method: ${escapeHtml(payment.payment_method)}</p>
        <p>Reference: ${escapeHtml(payment.id || receiptId)}</p>
      </div>
    </section>

    <section class="amount">
      <p class="muted">Amount received</p>
      <div class="amount-row">
        <span>Total</span>
        <span>${escapeHtml(formatMoney(Number(payment.amount || 0), currency))}</span>
      </div>
    </section>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th class="right">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Payment for ${escapeHtml(payment.property_name)}, Unit ${escapeHtml(payment.unit)}</td>
          <td class="right">${escapeHtml(formatMoney(Number(payment.amount || 0), currency))}</td>
        </tr>
      </tbody>
    </table>

    <p class="footer">${escapeHtml(payment.notes || "Thank you for your payment.")}</p>
  </main>
</body>
</html>`;
}

function printReceipt(payment, currency, onBlocked = () => {}) {
  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) {
    onBlocked();
    return;
  }

  printWindow.document.write(getReceiptHtml(payment, currency));
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

function downloadReceipt(payment, currency) {
  const blob = new Blob([getReceiptHtml(payment, currency)], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `${getReceiptId(payment)}.html`;
  link.click();
  URL.revokeObjectURL(url);
}

function emailReceipt(payment, currency, tenantEmail = "") {
  const receiptId = getReceiptId(payment);
  const subject = encodeURIComponent(`Receipt ${receiptId} from SpaceM8`);
  const body = encodeURIComponent(
    `Hello ${payment.tenant_name},\n\nThank you for your payment.\n\nReceipt: ${receiptId}\nInvoice: ${payment.invoice_id || "Unlinked"}\nProperty: ${payment.property_name}\nUnit: ${payment.unit}\nAmount: ${formatMoney(Number(payment.amount || 0), currency)}\nMethod: ${payment.payment_method}\nStatus: ${payment.status}\n\nSpaceM8`
  );

  window.location.href = `mailto:${encodeURIComponent(tenantEmail)}?subject=${subject}&body=${body}`;
}

async function syncInvoicePaymentStatus(invoiceId) {
  if (!invoiceId) return;

  const [{ data: invoice, error: invoiceError }, { data: linkedPayments, error: paymentsError }] =
    await Promise.all([
      supabase
        .from("invoices")
        .select("id, amount, due_date")
        .eq("id", invoiceId)
        .maybeSingle(),
      supabase
        .from("payments")
        .select("amount, status")
        .eq("invoice_id", invoiceId),
    ]);

  if (invoiceError || paymentsError || !invoice) {
    console.error("Unable to sync invoice payment status:", invoiceError || paymentsError);
    return;
  }

  const paidTotal = (linkedPayments || [])
    .filter((payment) => payment.status === "Paid")
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const invoiceAmount = Number(invoice.amount || 0);
  const today = new Date().toISOString().slice(0, 10);
  let nextStatus = "Outstanding";

  if (paidTotal >= invoiceAmount && invoiceAmount > 0) {
    nextStatus = "Paid";
  } else if (paidTotal > 0) {
    nextStatus = "Partial";
  } else if (invoice.due_date && invoice.due_date < today) {
    nextStatus = "Overdue";
  }

  const { error } = await supabase
    .from("invoices")
    .update({ status: nextStatus })
    .eq("id", invoiceId);

  if (error) {
    console.error("Unable to update invoice status:", error);
  }
}

function PaymentFormModal({
  isOpen,
  mode = "create",
  payment = null,
  tenants = [],
  invoices = [],
  onClose,
  onSaved,
  isDark,
  currency,
  currentOrganizationId,
}) {
  const toast = useToast();
  const [form, setForm] = useState({
    invoice_id: "",
    tenant_id: "",
    tenant_name: "",
    property_id: "",
    property_name: "",
    unit: "",
    amount: "",
    due_date: "",
    status: "Pending",
    payment_method: "Bank Transfer",
    notes: "",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (payment) {
      setForm({
        invoice_id: payment.invoice_id || "",
        tenant_id: payment.tenant_id || "",
        tenant_name: payment.tenant_name || "",
        property_id: payment.property_id || "",
        property_name: payment.property_name || "",
        unit: payment.unit || "",
        amount: payment.amount ?? "",
        due_date: payment.due_date || "",
        status: payment.status || "Pending",
        payment_method: payment.payment_method || "Bank Transfer",
        notes: payment.notes || "",
      });
    } else {
      setForm({
        invoice_id: "",
        tenant_id: "",
        tenant_name: "",
        property_id: "",
        property_name: "",
        unit: "",
        amount: "",
        due_date: "",
        status: "Pending",
        payment_method: "Bank Transfer",
        notes: "",
      });
    }
  }, [payment, isOpen]);

  if (!isOpen) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleTenantChange = (value) => {
    const selectedTenant = tenants.find((t) => t.id === value);

    setForm((prev) => ({
      ...prev,
      tenant_id: value,
      tenant_name: selectedTenant?.full_name || "",
      property_id: selectedTenant?.property_id || "",
      property_name: selectedTenant?.property_name || "",
      unit: selectedTenant?.unit || "",
      amount: selectedTenant?.monthly_rent ?? "",
    }));
  };

  const handleInvoiceChange = (value) => {
    const selectedInvoice = invoices.find((invoice) => invoice.id === value);
    const selectedTenant = tenants.find(
      (tenant) =>
        tenant.full_name === selectedInvoice?.tenant &&
        tenant.property_name === selectedInvoice?.property &&
        String(tenant.unit || "") === String(selectedInvoice?.unit || "")
    );
    const invoiceAmount = Number(selectedInvoice?.amount || 0);
    const paidAmount = Number(selectedInvoice?.paidAmount || 0);
    const balance = Math.max(invoiceAmount - paidAmount, 0);

    setForm((prev) => ({
      ...prev,
      invoice_id: value,
      tenant_id: selectedTenant?.id || prev.tenant_id,
      tenant_name: selectedInvoice?.tenant || prev.tenant_name,
      property_id: selectedTenant?.property_id || prev.property_id,
      property_name: selectedInvoice?.property || prev.property_name,
      unit: selectedInvoice?.unit || prev.unit,
      amount: balance || invoiceAmount || prev.amount,
      due_date: selectedInvoice?.due_date || prev.due_date,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      invoice_id: form.invoice_id || null,
      tenant_id: form.tenant_id || null,
      tenant_name: form.tenant_name,
      property_id: form.property_id || null,
      property_name: form.property_name,
      unit: form.unit,
      amount: Number(form.amount || 0),
      due_date: form.due_date,
      status: form.status,
      payment_method: form.payment_method,
      notes: form.notes,
      organization_id: payment?.organization_id || currentOrganizationId || null,
    };

    let error;

    if (mode === "edit" && payment?.id) {
      const result = await supabase.from("payments").update(payload).eq("id", payment.id);
      error = result.error;
    } else {
      const result = await supabase.from("payments").insert([payload]);
      error = result.error;
    }

    setSaving(false);

    if (error) {
      console.error(`${mode} payment error:`, error);
      toast.error(
        mode === "edit" ? "Payment update failed" : "Payment creation failed",
        "Please review the payment details and try again."
      );
      return;
    }

    await Promise.all([
      syncInvoicePaymentStatus(form.invoice_id),
      payment?.invoice_id && payment.invoice_id !== form.invoice_id
        ? syncInvoicePaymentStatus(payment.invoice_id)
        : Promise.resolve(),
    ]);

    await logActivity({
      action: mode === "edit" ? "Updated payment" : "Created payment",
      entityType: "payment",
      entityId: payment?.id || form.invoice_id || form.tenant_id,
      entityLabel: `${form.tenant_name} - ${formatMoney(Number(form.amount || 0), currency)}`,
      details: payload,
    });

    toast.success(
      mode === "edit" ? "Payment updated" : "Payment created",
      `${form.tenant_name} now has a ${formatMoney(Number(form.amount || 0), currency)} payment record.`
    );
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {mode === "edit" ? "Edit Payment" : "Add Payment"}
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {mode === "edit" ? "Update this payment record." : "Create a new payment record."}
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Link to invoice
            </label>
            <Select
              value={form.invoice_id}
              onChange={(e) => handleInvoiceChange(e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
            >
              <option value="">No invoice selected</option>
              {invoices.map((invoice) => {
                const balance = Math.max(Number(invoice.amount || 0) - Number(invoice.paidAmount || 0), 0);
                return (
                  <option key={invoice.id} value={invoice.id}>
                    {getInvoiceLabel(invoice)} - Balance {formatMoney(balance, currency)}
                  </option>
                );
              })}
            </Select>
          </div>

          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Tenant
            </label>
            <Select
              value={form.tenant_id}
              onChange={(e) => handleTenantChange(e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
              required
            >
              <option value="">Select tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.full_name} — {tenant.property_name} — Unit {tenant.unit}
                </option>
              ))}
            </Select>
          </div>

          <Input
            placeholder="Property"
            value={form.property_name}
            readOnly
            className={isDark ? "border-slate-700 bg-slate-800 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-700"}
          />

          <Input
            placeholder="Unit"
            value={form.unit}
            readOnly
            className={isDark ? "border-slate-700 bg-slate-800 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-700"}
          />

          <Input
            type="number"
            placeholder={`Amount (${currency})`}
            value={form.amount}
            onChange={(e) => update("amount", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Due date
            </label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => update("due_date", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
              required
            />
          </div>

          <Select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
          >
            <option>Paid</option>
            <option>Pending</option>
            <option>Overdue</option>
          </Select>

          <Select
            value={form.payment_method}
            onChange={(e) => update("payment_method", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
          >
            <option>Bank Transfer</option>
            <option>Mobile Money</option>
            <option>Cash</option>
            <option>Card</option>
          </Select>

          <div className="md:col-span-2">
            <textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Notes"
              className={`min-h-[120px] w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
                isDark
                  ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                  : "border-slate-200 bg-white text-slate-900"
              }`}
            />
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? (mode === "edit" ? "Updating..." : "Saving...") : mode === "edit" ? "Update Payment" : "Create Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeletePaymentModal({ isOpen, payment, onClose, onDeleted, isDark }) {
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  if (!isOpen || !payment) return null;

  const handleDelete = async () => {
    setDeleting(true);

    const { error } = await supabase.from("payments").delete().eq("id", payment.id);

    setDeleting(false);

    if (error) {
      console.error("Delete payment error:", error);
      toast.error("Payment deletion failed", "Please try again in a moment.");
      return;
    }

    await syncInvoicePaymentStatus(payment.invoice_id);
    await logActivity({
      action: "Deleted payment",
      entityType: "payment",
      entityId: payment.id,
      entityLabel: `${payment.tenant_name} - ${payment.amount}`,
      details: payment,
    });
    toast.success("Payment deleted", `The payment record for ${payment.tenant_name} has been removed.`);
    onDeleted();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-lg rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
          Delete Payment
        </h2>
        <p className={`mt-2 text-sm leading-7 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Are you sure you want to delete the payment record for{" "}
          <span className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
            {payment.tenant_name}
          </span>
          ?
        </p>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Cancel
          </button>

          <button
            onClick={handleDelete}
            disabled={deleting}
            className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
              deleting ? "bg-red-300" : "bg-red-500 hover:bg-red-600"
            }`}
          >
            {deleting ? "Deleting..." : "Delete Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptModal({ isOpen, payment, tenantEmail, onClose, isDark, currency }) {
  const toast = useToast();
  if (!isOpen || !payment) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Receipt {getReceiptId(payment)}
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Preview, print, save, or email this payment receipt.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className={`rounded-3xl border p-6 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"}`}>
          <div className={`flex flex-col gap-5 border-b pb-6 md:flex-row md:items-start md:justify-between ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <div className="flex items-center gap-4">
              <img src={logo} alt="SpaceM8" className="h-14 w-auto object-contain" />
              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  SpaceM8 Property Management
                </p>
                <h3 className={`mt-3 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  Payment Receipt
                </h3>
              </div>
            </div>

            <div className={`text-sm md:text-right ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{getReceiptId(payment)}</p>
              <p>Invoice: {payment.invoice_id || "Unlinked"}</p>
              <p>Status: {payment.status}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Received From
              </p>
              <p className={`mt-2 text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {payment.tenant_name}
              </p>
              <p className={isDark ? "text-slate-300" : "text-slate-600"}>{payment.property_name}</p>
              <p className={isDark ? "text-slate-300" : "text-slate-600"}>Unit {payment.unit}</p>
            </div>

            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Payment Details
              </p>
              <p className={`mt-2 ${isDark ? "text-slate-300" : "text-slate-600"}`}>Method: {payment.payment_method}</p>
              <p className={isDark ? "text-slate-300" : "text-slate-600"}>Date: {payment.due_date}</p>
              <p className={isDark ? "text-slate-300" : "text-slate-600"}>Email: {tenantEmail || "Not available"}</p>
            </div>
          </div>

          <div className={`mt-8 rounded-2xl border p-5 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-slate-50"}`}>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Amount received</p>
            <div className={`mt-2 flex items-center justify-between text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              <span>Total</span>
              <span>{formatMoney(Number(payment.amount || 0), currency)}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button
            onClick={() =>
              printReceipt(
                payment,
                currency,
                () =>
                  toast.error(
                    "Receipt could not open",
                    "Please allow pop-ups to print or save this receipt."
                  )
              )
            }
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-4 text-sm font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Printer className="mr-2 h-4 w-4" />
            Print / Save PDF
          </button>

          <button
            onClick={() => downloadReceipt(payment, currency)}
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-4 text-sm font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <FileText className="mr-2 h-4 w-4" />
            Save File
          </button>

          <button
            onClick={() => emailReceipt(payment, currency, tenantEmail)}
            className="inline-flex h-12 items-center justify-center rounded-2xl bg-orange-500 px-4 text-sm font-medium text-white transition hover:bg-orange-600"
          >
            <Mail className="mr-2 h-4 w-4" />
            Email Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentRow({ payment, isDark, currency, onEdit, onDelete, onReceipt }) {
  return (
    <div
      className={`grid items-center gap-4 border-t px-5 py-4 text-sm ${paymentsTableGridClass} ${
        isDark ? "border-slate-800" : "border-slate-200"
      }`}
    >
      <div>
        <p className={`font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
          {payment.tenant_name}
        </p>
        <p className={isDark ? "text-slate-400" : "text-slate-500"}>
          {payment.property_name}
        </p>
      </div>

      <span className={isDark ? "text-slate-300" : "text-slate-600"}>{payment.unit}</span>

      <span className={isDark ? "text-slate-300" : "text-slate-600"}>
        {payment.invoice_id || "Unlinked"}
      </span>

      <span className={`font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
        {formatMoney(Number(payment.amount || 0), currency)}
      </span>

      <span className={isDark ? "text-slate-300" : "text-slate-600"}>
        {payment.source === "intent"
          ? payment.displayDate
            ? `Requested ${String(payment.displayDate).slice(0, 10)}`
            : "Requested"
          : payment.due_date}
      </span>

      <span className={isDark ? "text-slate-300" : "text-slate-600"}>{payment.displayMethod || payment.payment_method}</span>

      <Badge className={getStatusClasses(payment.status)}>{payment.status}</Badge>

      <div className="flex items-center justify-center gap-2">
        {payment.source === "intent" ? (
          <span
            className={`rounded-xl border px-3 py-2 text-xs font-medium ${
              isDark
                ? "border-slate-700 text-slate-400"
                : "border-slate-200 text-slate-500"
            }`}
          >
            Awaiting callback
          </span>
        ) : (
          <>
            <button
              onClick={() => onReceipt(payment)}
              className={`rounded-xl border p-2 transition ${
                isDark
                  ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
              title="View receipt"
            >
              <FileText className="h-4 w-4" />
            </button>

            <button
              onClick={() => onEdit(payment)}
              className={`rounded-xl border p-2 transition ${
                isDark
                  ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
            >
              <Pencil className="h-4 w-4" />
            </button>

            <button
              onClick={() => onDelete(payment)}
              className={`rounded-xl border p-2 transition ${
                isDark
                  ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-red-400"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-red-600"
              }`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function PaymentsPage({
  isDark,
  setIsDark,
  currency,
  setCurrency,
}) {
  const toast = useToast();
  const { profile, user, sessionClaims } = useAuth();
  const [payments, setPayments] = useState([]);
  const [paymentIntents, setPaymentIntents] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [receiptPayment, setReceiptPayment] = useState(null);
  const [tenantEmails, setTenantEmails] = useState({});
  const [selectedReconciliationTargets, setSelectedReconciliationTargets] = useState({});
  const [reconcilingPaymentId, setReconcilingPaymentId] = useState(null);
  const normalizedRole = normalizeRole(
    profile?.role ||
      user?.app_metadata?.role ||
      user?.app_metadata?.app_role ||
      user?.user_metadata?.role ||
      user?.user_metadata?.app_role ||
      sessionClaims?.app_metadata?.role ||
      sessionClaims?.app_metadata?.app_role ||
      sessionClaims?.user_metadata?.role ||
      sessionClaims?.user_metadata?.app_role
  );
  const currentOrganizationId = extractOrganizationId(profile, user, sessionClaims);
  const canReconcilePayments = ["Growth", "Administrator", "Accountant"].includes(normalizedRole);

  const fetchPaymentsAndTenants = useCallback(async () => {
    setLoading(true);

    if (!currentOrganizationId) {
      setPayments([]);
      setPaymentIntents([]);
      setTenants([]);
      setInvoices([]);
      setTenantEmails({});
      setLoading(false);
      return;
    }

    let paymentsQuery = supabase
      .from("payments")
      .select("*")
      .order("created_at", { ascending: false });

    let paymentIntentsQuery = supabase
      .from("payment_intents")
      .select("id, tenant_id, invoice_id, amount, currency, provider, channel, phone_number, external_reference, status, created_at, updated_at")
      .order("created_at", { ascending: false });

    let tenantsQuery = supabase
      .from("tenants")
      .select("id, full_name, email, property_id, property_name, unit, monthly_rent")
      .order("full_name", { ascending: true });

    let invoicesQuery = supabase
      .from("invoices")
      .select("id, tenant, property, unit, amount, amount_paid, balance_due, due_date, status, external_reference")
      .order("created_at", { ascending: false });

    if (currentOrganizationId) {
      paymentsQuery = paymentsQuery.eq("organization_id", currentOrganizationId);
      paymentIntentsQuery = paymentIntentsQuery.eq("organization_id", currentOrganizationId);
      tenantsQuery = tenantsQuery.eq("organization_id", currentOrganizationId);
      invoicesQuery = invoicesQuery.eq("organization_id", currentOrganizationId);
    }

    const [
      { data: paymentsData, error: paymentsError },
      { data: paymentIntentsData, error: paymentIntentsError },
      { data: tenantsData, error: tenantsError },
      { data: invoicesData, error: invoicesError },
    ] =
      await Promise.all([
        paymentsQuery,
        paymentIntentsQuery,
        tenantsQuery,
        invoicesQuery,
      ]);

    if (paymentsError) {
      console.error("Error fetching payments:", paymentsError);
    } else {
      setPayments(paymentsData || []);
    }

    if (paymentIntentsError) {
      console.error("Error fetching payment intents:", paymentIntentsError);
      setPaymentIntents([]);
    } else {
      setPaymentIntents(paymentIntentsData || []);
    }

    if (tenantsError) {
      console.error("Error fetching tenants for payments:", tenantsError);
    } else {
      setTenants(tenantsData || []);
      setTenantEmails(
        (tenantsData || []).reduce((emails, tenant) => {
          if (tenant.full_name && tenant.email) {
            emails[tenant.full_name] = tenant.email;
          }
          return emails;
        }, {})
      );
    }

    if (invoicesError) {
      console.error("Error fetching invoices for payments:", invoicesError);
      setInvoices([]);
    } else {
      const paidByInvoice = (paymentsData || []).reduce((totalsByInvoice, payment) => {
        if (payment.invoice_id && payment.status === "Paid") {
          totalsByInvoice[payment.invoice_id] =
            (totalsByInvoice[payment.invoice_id] || 0) + Number(payment.amount || 0);
        }
        return totalsByInvoice;
      }, {});

      setInvoices(
        (invoicesData || []).map((invoice) => ({
          ...invoice,
          paidAmount: paidByInvoice[invoice.id] || 0,
          outstandingAmount: getOutstandingAmount({
            ...invoice,
            paidAmount: paidByInvoice[invoice.id] || Number(invoice.amount_paid || 0),
          }),
        }))
      );
    }

    setLoading(false);
  }, [currentOrganizationId]);

  useEffect(() => {
    fetchPaymentsAndTenants();
  }, [fetchPaymentsAndTenants]);

  const totals = useMemo(() => {
    const totalCollected = payments
      .filter((item) => item.status === "Paid")
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const totalPendingPayments = payments
      .filter((item) => item.status === "Pending")
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const pendingPaymentInvoiceIds = new Set(
      payments
        .filter((item) => String(item.status || "").toLowerCase() === "pending")
        .map((item) => item.invoice_id)
        .filter(Boolean)
    );

    const pendingIntentAmount = paymentIntents
      .filter((intent) => {
        const normalizedStatus = String(intent.status || "").toLowerCase();
        return ["pending", "submitted"].includes(normalizedStatus) && !pendingPaymentInvoiceIds.has(intent.invoice_id);
      })
      .reduce((sum, intent) => sum + Number(intent.amount || 0), 0);

    const totalOverdue = payments
      .filter((item) => item.status === "Overdue")
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);

    return {
      totalCollected,
      totalPending: totalPendingPayments + pendingIntentAmount,
      totalOverdue,
      totalRecords:
        payments.length +
        paymentIntents.filter((intent) => {
          const normalizedStatus = String(intent.status || "").toLowerCase();
          return ["pending", "submitted"].includes(normalizedStatus) && !pendingPaymentInvoiceIds.has(intent.invoice_id);
        }).length,
    };
  }, [payments, paymentIntents]);

  const paymentRecords = useMemo(() => {
    const tenantLookup = new Map((tenants || []).map((tenant) => [tenant.id, tenant]));
    const invoiceLookup = new Map((invoices || []).map((invoice) => [invoice.id, invoice]));

    const normalizedPayments = (payments || []).map((payment) => ({
      ...payment,
      source: "payment",
      actionLocked: false,
      displayMethod: payment.payment_method || "Not set",
      displayDate: payment.due_date || payment.payment_date || payment.created_at || "",
      reference: payment.provider_transaction_id || payment.reference || "",
    }));

    const existingPendingInvoiceIds = new Set(
      normalizedPayments
        .filter((payment) => String(payment.status || "").toLowerCase() === "pending")
        .map((payment) => payment.invoice_id)
        .filter(Boolean)
    );

    const normalizedIntents = (paymentIntents || [])
      .filter((intent) => {
        const normalizedStatus = String(intent.status || "").toLowerCase();
        return ["pending", "submitted"].includes(normalizedStatus) && !existingPendingInvoiceIds.has(intent.invoice_id);
      })
      .map((intent) => {
        const linkedInvoice = invoiceLookup.get(intent.invoice_id) || null;
        const linkedTenant = tenantLookup.get(intent.tenant_id) || null;

        return {
          id: `intent-${intent.id}`,
          sourceId: intent.id,
          source: "intent",
          tenant_name: linkedInvoice?.tenant || linkedTenant?.full_name || "Pending tenant",
          property_name: linkedInvoice?.property || linkedTenant?.property_name || "Pending property",
          unit: linkedInvoice?.unit || linkedTenant?.unit || "",
          invoice_id: intent.invoice_id || "",
          amount: Number(intent.amount || 0),
          due_date: linkedInvoice?.due_date || "",
          status: "Pending",
          payment_method: "M-Pesa request",
          displayMethod: "M-Pesa request",
          displayDate: intent.created_at || "",
          created_at: intent.created_at || "",
          notes: "Payment request created and awaiting collection.",
          reference: intent.external_reference || "",
          actionLocked: true,
        };
      });

    return [...normalizedIntents, ...normalizedPayments].sort((left, right) => {
      const leftTime = new Date(left.created_at || left.displayDate || 0).getTime();
      const rightTime = new Date(right.created_at || right.displayDate || 0).getTime();
      return rightTime - leftTime;
    });
  }, [payments, paymentIntents, tenants, invoices]);

  const filteredPayments = useMemo(() => {
    return paymentRecords.filter((payment) => {
      const matchesQuery =
        payment.tenant_name?.toLowerCase().includes(query.toLowerCase()) ||
        payment.property_name?.toLowerCase().includes(query.toLowerCase()) ||
        payment.unit?.toLowerCase().includes(query.toLowerCase()) ||
        payment.invoice_id?.toLowerCase().includes(query.toLowerCase());

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Unmatched" ? isUnmatchedPayment(payment) : payment.status === statusFilter);

      return matchesQuery && matchesStatus;
    });
  }, [paymentRecords, query, statusFilter]);

  const unmatchedPayments = useMemo(
    () => payments.filter((payment) => isUnmatchedPayment(payment)),
    [payments]
  );

  const handleReconcilePayment = useCallback(
    async (payment, preferredInvoiceId = "") => {
      const invoiceId = preferredInvoiceId || selectedReconciliationTargets[payment.id];

      if (!invoiceId) {
        toast.error("Choose an invoice", "Select the invoice this payment should be matched to first.");
        return;
      }

      const invoice = invoices.find((item) => item.id === invoiceId);
      if (!invoice) {
        toast.error("Invoice not found", "The selected invoice could not be loaded for reconciliation.");
        return;
      }

      const outstandingAmount = getOutstandingAmount(invoice);
      const paymentAmount = Number(payment.amount || 0);

      if (outstandingAmount <= 0) {
        toast.error("Invoice already cleared", "That invoice no longer has an outstanding balance to reconcile.");
        return;
      }

      if (paymentAmount > outstandingAmount) {
        toast.error(
          "Overpayment needs review",
          "This payment is larger than the invoice balance. Leave it flagged for now until tenant credit handling is added."
        );
        return;
      }

      const tenantMatch = tenants.find(
        (tenant) =>
          tenant.full_name === invoice.tenant &&
          tenant.property_name === invoice.property &&
          String(tenant.unit || "") === String(invoice.unit || "")
      );

      if (!tenantMatch) {
        toast.error("Tenant could not be resolved", "SpaceM8 could not match that invoice back to a tenant record.");
        return;
      }

      setReconcilingPaymentId(payment.id);

      try {
        const paymentUpdate = {
          invoice_id: invoice.id,
          tenant_id: tenantMatch.id,
          tenant_name: tenantMatch.full_name,
          property_id: tenantMatch.property_id || payment.property_id || null,
          property_name: tenantMatch.property_name || invoice.property || null,
          unit: tenantMatch.unit || invoice.unit || null,
          due_date: invoice.due_date || payment.due_date || null,
          status: "Paid",
          unmatched_reason: null,
          notes: `${payment.notes ? `${payment.notes} ` : ""}Reconciled manually to invoice ${invoice.id}.`.trim(),
        };

        const { error: paymentError } = await supabase
          .from("payments")
          .update(paymentUpdate)
          .eq("id", payment.id);

        if (paymentError) {
          throw new Error(paymentError.message || "Payment could not be updated.");
        }

        const { data: existingAllocation, error: allocationLookupError } = await supabase
          .from("payment_allocations")
          .select("id")
          .eq("payment_id", payment.id)
          .eq("invoice_id", invoice.id)
          .limit(1)
          .maybeSingle();

        if (allocationLookupError) {
          throw new Error(allocationLookupError.message || "Existing allocation lookup failed.");
        }

        if (!existingAllocation) {
          const { error: allocationError } = await supabase.from("payment_allocations").insert({
            organization_id: payment.organization_id || currentOrganizationId,
            payment_id: payment.id,
            invoice_id: invoice.id,
            allocated_amount: paymentAmount,
          });

          if (allocationError) {
            throw new Error(allocationError.message || "Payment allocation could not be created.");
          }
        }

        const updatedAmountPaid = Number((Number(invoice.amount_paid || 0) + paymentAmount).toFixed(2));
        const updatedBalance = Number(Math.max(Number(invoice.amount || 0) - updatedAmountPaid, 0).toFixed(2));
        const updatedInvoiceStatus = updatedBalance <= 0 ? "Paid" : updatedAmountPaid > 0 ? "Partial" : invoice.status || "Outstanding";

        const { error: invoiceError } = await supabase
          .from("invoices")
          .update({
            amount_paid: updatedAmountPaid,
            balance_due: updatedBalance,
            status: updatedInvoiceStatus,
          })
          .eq("id", invoice.id);

        if (invoiceError) {
          throw new Error(invoiceError.message || "Invoice could not be updated.");
        }

        const { data: existingReceipt, error: receiptLookupError } = await supabase
          .from("receipts")
          .select("id")
          .eq("payment_id", payment.id)
          .limit(1)
          .maybeSingle();

        if (receiptLookupError) {
          throw new Error(receiptLookupError.message || "Receipt lookup failed.");
        }

        if (!existingReceipt) {
          const { error: receiptError } = await supabase.from("receipts").insert({
            organization_id: payment.organization_id || currentOrganizationId,
            payment_id: payment.id,
            receipt_number: getReceiptId(payment),
          });

          if (receiptError) {
            throw new Error(receiptError.message || "Receipt could not be created.");
          }
        }

        await logActivity({
          action: "Reconciled unmatched payment",
          entityType: "payment",
          entityId: payment.id,
          entityLabel: payment.provider_transaction_id || payment.id,
          details: {
            paymentId: payment.id,
            invoiceId: invoice.id,
            tenantId: tenantMatch.id,
            organizationId: payment.organization_id || currentOrganizationId,
            amount: paymentAmount,
          },
        });

        setSelectedReconciliationTargets((prev) => {
          const next = { ...prev };
          delete next[payment.id];
          return next;
        });

        toast.success("Payment reconciled", `The flagged payment has been attached to invoice ${invoice.id}.`);
        fetchPaymentsAndTenants();
      } catch (error) {
        console.error("Reconcile payment error:", error);
        toast.error("Reconciliation failed", error instanceof Error ? error.message : "Please try again.");
      } finally {
        setReconcilingPaymentId(null);
      }
    },
    [currentOrganizationId, fetchPaymentsAndTenants, invoices, selectedReconciliationTargets, tenants, toast]
  );

  if (loading) {
    return (
      <AppLayout
        isDark={isDark}
        setIsDark={setIsDark}
        currency={currency}
        setCurrency={setCurrency}
        eyebrow="Collections Control"
        title="Payments"
        description="Track collections, outstanding balances, due dates, and payment status across the portfolio."
      >
        <div className={isDark ? "text-slate-300" : "text-slate-600"}>
          Loading payments...
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Collections Control"
      title="Payments"
      description="Track collections, outstanding balances, due dates, and payment status across the portfolio."
      actions={
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition hover:bg-orange-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Payment
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {[
          {
            label: "Collected",
            value: formatMoney(totals.totalCollected, currency),
            icon: CheckCircle2,
            tone: "bg-emerald-100 text-emerald-700",
          },
          {
            label: "Pending",
            value: formatMoney(totals.totalPending, currency),
            icon: Clock3,
            tone: "bg-sky-100 text-sky-700",
          },
          {
            label: "Overdue",
            value: formatMoney(totals.totalOverdue, currency),
            icon: AlertCircle,
            tone: "bg-amber-100 text-amber-700",
          },
          {
            label: "Records",
            value: `${totals.totalRecords}`,
            icon: Wallet,
            tone: "bg-orange-100 text-orange-700",
          },
        ].map((item, index) => {
          const Icon = item.icon;

          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={`rounded-[2rem] border p-5 shadow-md transition-shadow hover:shadow-lg ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {item.label}
                  </p>
                  <h3 className={`mt-3 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                    {item.value}
                  </h3>
                </div>

                <div className={`rounded-2xl p-3 ${item.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      {canReconcilePayments ? (
        <section
          className={`rounded-[2rem] border p-5 shadow-sm ${
            isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
          }`}
        >
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Reconciliation queue
              </p>
              <h2 className={`mt-2 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                Unmatched payments
              </h2>
              <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Review direct Paybill or callback payments that need to be attached to the correct invoice.
              </p>
            </div>

            <Badge className={unmatchedPayments.length > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}>
              {unmatchedPayments.length} awaiting review
            </Badge>
          </div>

          {unmatchedPayments.length === 0 ? (
            <div
              className={`mt-5 rounded-[1.5rem] border px-5 py-6 text-sm ${
                isDark ? "border-slate-800 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              No flagged payments right now. Incoming Paybill records that fail automatic matching will appear here.
            </div>
          ) : (
            <div className="mt-5 grid gap-4">
              {unmatchedPayments.map((payment) => {
                const candidateInvoices = [...invoices]
                  .filter((invoice) => getOutstandingAmount(invoice) > 0)
                  .map((invoice) => ({
                    invoice,
                    score: scoreReconciliationInvoice(payment, invoice),
                  }))
                  .filter((candidate) => candidate.score > 0)
                  .sort((a, b) => b.score - a.score);
                const suggestedInvoice = candidateInvoices[0]?.invoice || null;
                const selectedInvoiceId = selectedReconciliationTargets[payment.id] || suggestedInvoice?.id || "";

                return (
                  <div
                    key={payment.id}
                    className={`rounded-[1.5rem] border p-4 ${
                      isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className="bg-rose-100 text-rose-700">
                            {formatUnmatchedReason(payment.unmatched_reason)}
                          </Badge>
                          <Badge className={getStatusClasses(payment.status)}>{payment.status}</Badge>
                          <span className={`text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                            {payment.provider_transaction_id || payment.id}
                          </span>
                        </div>

                        <div>
                          <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                            {formatMoney(Number(payment.amount || 0), currency)}
                          </p>
                          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                            Reference: {payment.reference || "Not supplied"} • Phone: {payment.payer_phone || "Unknown"}
                          </p>
                          {suggestedInvoice ? (
                            <p className={`mt-1 text-xs font-medium ${isDark ? "text-sky-300" : "text-sky-700"}`}>
                              Suggested match: {suggestedInvoice.id} • {suggestedInvoice.tenant} • Balance{" "}
                              {formatMoney(getOutstandingAmount(suggestedInvoice), currency)}
                            </p>
                          ) : null}
                        </div>

                        <div className={`grid gap-2 text-sm md:grid-cols-3 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                          <p>Tenant hint: {payment.tenant_name || "Not matched yet"}</p>
                          <p>Property hint: {payment.property_name || "Not matched yet"}</p>
                          <p>Due date: {payment.due_date || "Not set"}</p>
                        </div>
                      </div>

                      <div className="w-full max-w-xl">
                        <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                          Attach to invoice
                        </label>
                        <div className="flex flex-col gap-3 md:flex-row">
                          <Select
                            value={selectedInvoiceId}
                            onChange={(e) =>
                              setSelectedReconciliationTargets((prev) => ({
                                ...prev,
                                [payment.id]: e.target.value,
                              }))
                            }
                            className={
                              isDark
                                ? "border-slate-700 bg-slate-900 text-white"
                                : "border-slate-200 bg-white text-slate-900"
                            }
                          >
                            <option value="">Select invoice</option>
                            {(candidateInvoices.length > 0
                              ? candidateInvoices.map((candidate) => candidate.invoice)
                              : invoices.filter((invoice) => getOutstandingAmount(invoice) > 0)
                            ).map((invoice) => (
                                <option key={invoice.id} value={invoice.id}>
                                  {invoice.id} - {invoice.tenant} - {invoice.property} - Balance {formatMoney(getOutstandingAmount(invoice), currency)}
                                </option>
                              ))}
                          </Select>

                          <button
                            onClick={() => handleReconcilePayment(payment, selectedInvoiceId)}
                            disabled={!selectedInvoiceId || reconcilingPaymentId === payment.id}
                            className="inline-flex h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-orange-300"
                          >
                            <Link2 className="mr-2 h-4 w-4" />
                            {reconcilingPaymentId === payment.id ? "Reconciling..." : "Match payment"}
                          </button>
                        </div>

                        <p className={`mt-2 text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                          SpaceM8 will attach the payment, create an allocation, update the invoice balance, and clear the flag.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : null}

      <section
        className={`rounded-[2rem] border p-4 shadow-sm md:p-5 ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by tenant, property, or unit..."
                className={`h-12 pl-11 pr-4 ${
                  isDark
                    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                    : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                }`}
              />
            </div>

            <div className="flex items-center gap-2">
              {["All", "Paid", "Pending", "Recorded", "Unmatched", "Flagged", "Overdue"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                    statusFilter === status
                      ? "bg-sky-100 text-sky-700"
                      : isDark
                      ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className={`overflow-hidden rounded-[2rem] border shadow-sm ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div
          className={`hidden items-center gap-4 px-5 py-4 text-sm md:grid ${paymentsTableGridClass} ${
            isDark ? "bg-slate-950 text-slate-400" : "bg-slate-50 text-slate-600"
          }`}
        >
          <span>Tenant / Property</span>
          <span>Unit</span>
          <span>Invoice</span>
          <span>Amount</span>
          <span>Due date</span>
          <span>Method</span>
          <span>Status</span>
          <span className="text-center">Actions</span>
        </div>

        {filteredPayments.length === 0 ? (
          <div className={`p-8 text-center ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            No payments found.
          </div>
        ) : (
          filteredPayments.map((payment) => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              isDark={isDark}
              currency={currency}
              onEdit={setEditingPayment}
              onDelete={setDeletingPayment}
              onReceipt={setReceiptPayment}
            />
          ))
        )}
      </section>

      <PaymentFormModal
        isOpen={isCreateOpen}
        mode="create"
        tenants={tenants}
        invoices={invoices}
        onClose={() => setIsCreateOpen(false)}
        onSaved={fetchPaymentsAndTenants}
        isDark={isDark}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
      />

      <PaymentFormModal
        isOpen={!!editingPayment}
        mode="edit"
        payment={editingPayment}
        tenants={tenants}
        invoices={invoices}
        onClose={() => setEditingPayment(null)}
        onSaved={fetchPaymentsAndTenants}
        isDark={isDark}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
      />

      <DeletePaymentModal
        isOpen={!!deletingPayment}
        payment={deletingPayment}
        onClose={() => setDeletingPayment(null)}
        onDeleted={fetchPaymentsAndTenants}
        isDark={isDark}
      />

      <ReceiptModal
        isOpen={!!receiptPayment}
        payment={receiptPayment}
        tenantEmail={receiptPayment ? tenantEmails[receiptPayment.tenant_name] : ""}
        onClose={() => setReceiptPayment(null)}
        isDark={isDark}
        currency={currency}
      />
    </AppLayout>
  );
}
